import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Wallet, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Balance = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/balance`, { withCredentials: true });
      setBalance(data);
    } catch (error) {
      toast.error('Eroare la încărcarea soldului');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  const unpaidInvoices = balance?.invoices.filter((inv) => !inv.is_paid) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Outfit' }}>
          Sold și Facturi
        </h1>

        <Card className="mb-8 border border-slate-200 shadow-lg" data-testid="balance-card">
          <CardHeader className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <Wallet className="w-8 h-8" />
              </div>
              <div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>
                  Sold Total Neachitat
                </CardTitle>
                <p className="text-blue-100 text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>
                  Suma totală pentru toate facturile neplătite
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <p
              className="text-5xl font-bold text-blue-900"
              style={{ fontFamily: 'Outfit' }}
              data-testid="unpaid-total"
            >
              {balance?.unpaid_total.toFixed(2)} RON
            </p>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
          Facturi Neplătite
        </h2>

        {unpaidInvoices.length === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <p className="text-green-800 font-semibold" style={{ fontFamily: 'IBM Plex Sans' }}>
              ✓ Nu ai facturi neplătite!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {unpaidInvoices.map((invoice) => {
              const dueDate = new Date(invoice.due_date);
              const isOverdue = dueDate < new Date();

              return (
                <Card
                  key={invoice.id}
                  className={`border shadow-sm ${
                    isOverdue ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  data-testid={`unpaid-invoice-${invoice.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-bold text-lg" style={{ fontFamily: 'Outfit' }}>
                            Factură #{(invoice.order_id || invoice.id).substring(0, 8).toUpperCase()}
                          </h3>
                          {isOverdue && (
                            <Badge className="bg-red-600 text-white">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Depășită
                            </Badge>
                          )}
                          {(invoice.paid_amount || 0) > 0 && !invoice.is_paid && (
                            <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">
                              Parțial achitată
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                          {invoice.original_filename}
                        </p>
                        <p
                          className="text-sm font-semibold text-slate-700"
                          style={{ fontFamily: 'IBM Plex Sans' }}
                        >
                          Scadență: {format(dueDate, 'dd MMMM yyyy', { locale: ro })}
                        </p>
                        {(invoice.paid_amount || 0) > 0 && (
                          <p className="text-xs text-green-700" style={{ fontFamily: 'IBM Plex Sans' }}>
                            Achitat: {(invoice.paid_amount || 0).toFixed(2)} RON din {invoice.amount.toFixed(2)} RON
                          </p>
                        )}
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>Rest de plată</p>
                        <p
                          className="text-3xl font-bold text-blue-900"
                          style={{ fontFamily: 'Outfit' }}
                          data-testid={`unpaid-invoice-amount-${invoice.id}`}
                        >
                          {(invoice.amount - (invoice.paid_amount || 0)).toFixed(2)} RON
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Balance;